import { X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "../../../lib/utils.js";

export type ActiveFilterChip = {
  id: string;
  label: string;
  value: string;
  onRemove: () => void;
};

export function activeFilterChip(
  id: string,
  label: string,
  value: string | null | undefined,
  onRemove: () => void,
): ActiveFilterChip | null {
  const trimmed = value?.trim() ?? "";
  if (!trimmed) return null;
  return { id, label, value: trimmed, onRemove };
}

export function ActiveFilterBar({
  chips,
  onClearAll,
  clearTestId,
  className,
}: {
  chips: readonly ActiveFilterChip[];
  onClearAll: () => void;
  clearTestId?: string;
  className?: string;
}): React.JSX.Element | null {
  const { t } = useTranslation();
  if (chips.length === 0) return null;

  return (
    <div
      className={cn(
        "flex flex-wrap items-center gap-1.5 border-b border-line/40 bg-[linear-gradient(180deg,rgba(231,246,240,0.55)_0%,rgba(245,248,248,0.2)_100%)] px-4 py-1.5",
        className,
      )}
      data-testid="active-filter-bar"
    >
      <ul className="m-0 flex min-w-0 flex-1 list-none flex-wrap items-center gap-1.5 p-0">
        {chips.map((chip) => (
          <li key={chip.id} className="max-w-full">
            <span className="inline-flex max-w-full items-center gap-1 rounded-full border border-line/80 bg-surface py-0.5 ps-2.5 pe-0.5 text-[0.75rem] shadow-[0_1px_1px_rgb(15_23_42/0.04)]">
              <span className="shrink-0 text-muted">{chip.label}</span>
              <span className="max-w-[14rem] truncate font-semibold text-foreground">{chip.value}</span>
              <button
                type="button"
                className="inline-grid size-5 shrink-0 place-items-center rounded-full text-muted transition-colors hover:bg-surface-muted hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-accent/40"
                onClick={chip.onRemove}
                aria-label={t("app.activeFilters.remove", { name: `${chip.label}: ${chip.value}` })}
              >
                <X size={12} aria-hidden="true" />
              </button>
            </span>
          </li>
        ))}
      </ul>
      <button
        type="button"
        className="shrink-0 rounded-full px-2 py-1 text-[0.75rem] font-semibold text-accent transition-colors hover:bg-accent-soft hover:text-accent-hover focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-accent/40"
        onClick={onClearAll}
        data-testid={clearTestId}
      >
        {t("app.activeFilters.clearAll")}
      </button>
    </div>
  );
}
